import React, { useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';

import { usePortalContext } from '../../context/PortalContext';
import { auth } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import { getPortalDayKey, logClientPortalEvent, logClientPortalEventOnce } from '../../utils/portalEventLog';
import { updateClientConsentLegacy, updateClientConsent, setClientConsent, getConsentPatch } from '../../services/ConsentGateRepository';

const LOCAL_CONSENT_KEY = 'rda_portal_consent_v1';

export default function ConsentGate({ children }) {
  const { crmClient, loading } = usePortalContext();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [optimisticAccepted, setOptimisticAccepted] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(LOCAL_CONSENT_KEY) === 'accepted';
  });

  const needsConsent = useMemo(() => {
    if (optimisticAccepted) return false;
    if (loading) return false;
    // GHOST LOCK FIX: durante a seleção de perfil (multi-tenant), crmClient = null.
    // Sem este guard, o overlay fixed z-[9999] cobre toda a tela e silencia os cliques.
    if (!crmClient) return false;
    const v = crmClient?.metadata?.hasConsented;
    return v !== true;
  }, [crmClient, loading, optimisticAccepted]);

  const handleAccept = async () => {
    if (saving) return;

    setError('');
    setOptimisticAccepted(true);

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCAL_CONSENT_KEY, 'accepted');
      }
    } catch (e) {
      void e;
    }

    setSaving(true);

    try {
      const clientId = String(crmClient?.id || '').trim();
      if (!clientId) return;

      const patch = getConsentPatch();

      let wrote = false;

      try {
        await updateClientConsentLegacy(clientId, patch);
        wrote = true;
      } catch (e) {
        void e;
      }

      const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
      if (appId) {
        try {
          await updateClientConsent(clientId, patch, appId);
          wrote = true;
        } catch (e) {
          try {
            await setClientConsent(clientId, patch, appId);
            wrote = true;
          } catch (e2) {
            void e;
            void e2;
          }
        }
      }

      if (!wrote) {
        throw new Error('Não foi possível localizar o documento do cliente para atualizar o consentimento.');
      }

      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_CONSENT_ACCEPTED',
          actorName: 'Cliente',
          payload: {
            text: 'Consentimento e privacidade aceitos pelo cliente no portal.',
          },
          storage: 'local',
          dedupeKey: getPortalDayKey(),
        });
      } catch (e) {
        void e;
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const handleDecline = async () => {
    if (saving) return;
    try {
      const clientId = String(crmClient?.id || '').trim();
      if (clientId) {
        await logClientPortalEvent({
          clientId,
          type: 'CLIENT_CONSENT_DECLINED',
          actorName: 'Cliente',
          payload: {
            text: 'Cliente recusou o consentimento e saiu do portal.',
          },
        });
      }
    } catch (e) {
      void e;
    }
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(LOCAL_CONSENT_KEY);
      }
    } catch (e) {
      void e;
    }
    try {
      await signOut(auth);
    } catch (e) {
      void e;
    }

    window.location.replace('/');
  };

  return (
    <div className="relative">
      {children}

      {needsConsent ? (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl border border-gray-200 bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
              <div className="text-sm font-extrabold text-[#20243B]">Termo de Consentimento e Privacidade</div>
              <div className="mt-1 text-xs text-gray-600">
                Para acessar o Portal, é necessário aceitar as condições.
              </div>
            </div>

            <div className="p-5">
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 max-h-[50vh] overflow-y-auto text-xs text-gray-700 leading-relaxed space-y-3">
                <p>
                  Ao utilizar este Portal do Cliente, você declara estar ciente de que informações e documentos podem conter
                  dados pessoais e dados sensíveis. O acesso é destinado exclusivamente ao titular e/ou representantes
                  autorizados.
                </p>
                <p>
                  O escritório adotará medidas técnicas e organizacionais razoáveis para proteger os dados, mas o cliente
                  também deve manter sigilo do link de acesso, do dispositivo e do e-mail associado.
                </p>
                <p>
                  Finalidades: comunicação, acompanhamento do caso, troca de documentos e registro de atividades.
                  Compartilhamento: limitado ao necessário para a execução do serviço jurídico e obrigações legais.
                </p>
                <p>
                  Você pode solicitar informações adicionais e exercer seus direitos previstos na LGPD. Em caso de dúvidas,
                  contate o escritório.
                </p>
              </div>

              {error ? (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 whitespace-pre-wrap">
                  {error}
                </div>
              ) : null}

              <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleDecline}
                  className={
                    'rounded-lg px-4 py-3 text-xs font-extrabold uppercase tracking-widest border ' +
                    (saving
                      ? 'border-gray-200 text-gray-400 bg-gray-100'
                      : 'border-red-200 text-red-700 bg-white hover:bg-red-50')
                  }
                >
                  Não Concordo e Sair
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleAccept}
                  className={
                    'rounded-lg px-4 py-3 text-xs font-extrabold uppercase tracking-widest text-white ' +
                    (saving ? 'bg-[#20243B]/70' : 'bg-[#20243B] hover:bg-[#20243B]/90')
                  }
                >
                  {saving ? 'Registrando...' : 'Li e Aceito as Condições'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
